import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Package, Users, FileText, BarChart3, Wrench, Activity, Menu, X, Settings } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';
import { usePermissions, PERMISSIONS } from '@/hooks/usePermissions';

interface AppLayoutProps {
  children: React.ReactNode;
  currentPage: 'dashboard' | 'inventory' | 'wholesale' | 'reports' | 'settings';
}

export const AppLayout = ({ children, currentPage }: AppLayoutProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const isMobile = useIsMobile();
  const { user, hasPermission, signOut } = usePermissions();
  

  const navigationItems = [
    { name: 'Dashboard', path: '/dashboard', icon: Activity, permission: PERMISSIONS.VIEW_DASHBOARD, page: 'dashboard' },
    { name: 'Inventory', path: '/inventory', icon: Package, permission: PERMISSIONS.VIEW_DASHBOARD, page: 'inventory' },
    { name: 'Wholesale', path: '/wholesale', icon: Users, permission: PERMISSIONS.VIEW_DASHBOARD, page: 'wholesale' },
    { name: 'Reports', path: '/reports', icon: BarChart3, permission: PERMISSIONS.VIEW_REPORTS, page: 'reports' },
    { name: 'Settings', path: '/settings', icon: Settings, permission: PERMISSIONS.VIEW_SETTINGS, page: 'settings' },
  ];

  const NavigationContent = () => (
    <nav className="space-y-1">
      {navigationItems.map((item) => (
        <Button
          key={item.path}
          variant={currentPage === item.page ? 'default' : 'ghost'}
          className="w-full justify-start text-left mobile-nav-item touch-target tap-highlight-none hover-desktop"
          asChild
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <Link to={item.path}>
            <item.icon className="mr-3 h-4 w-4 flex-shrink-0" />
            <span className="truncate text-responsive-sm">{item.name}</span>
          </Link>
        </Button>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 safe-area-top">
        <div className="flex h-14 sm:h-16 items-center justify-between px-3 sm:px-4 md:px-6">
          <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
            {/* Mobile menu trigger */}
            <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="sm" className="md:hidden touch-target shrink-0 h-9 w-9 p-0">
                  <Menu className="h-5 w-5" />
                  <span className="sr-only">Toggle navigation menu</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[280px] p-0 safe-area-top">
                <div className="flex h-14 sm:h-16 items-center justify-between px-4 border-b">
                  <div className="flex items-center space-x-3">
                    <img 
                      src="/lovable-uploads/eec58224-93bd-48d5-bb38-e8fa7b140620.png" 
                      alt="Blue Whale Enterprises Logo" 
                      className="h-8 w-8"
                    />
                    <span className="font-semibold text-sm">Blue Whale</span>
                  </div>
                </div>
                <div className="p-4 sm:p-6">
                  <NavigationContent />
                </div>
              </SheetContent>
            </Sheet>
            
            <img 
              src="/lovable-uploads/eec58224-93bd-48d5-bb38-e8fa7b140620.png" 
              alt="Blue Whale Enterprises Logo" 
              className="h-7 w-7 sm:h-8 sm:w-8 shrink-0"
            />
            <span className="font-bold text-xs sm:text-sm md:text-lg truncate min-w-0">
              Blue Whale Enterprises
            </span>
          </div>
          
          <div className="flex items-center space-x-1 sm:space-x-2 shrink-0">
            <span className="text-xs sm:text-sm text-muted-foreground hidden sm:block truncate max-w-[100px] md:max-w-[200px]">
              {user?.email}
            </span>
            <Button variant="outline" size="sm" onClick={signOut} className="touch-target text-xs sm:text-sm h-9">
              <span className="hidden sm:inline">Sign Out</span>
              <span className="sm:hidden">Exit</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="flex h-[calc(100vh-3.5rem)] sm:h-[calc(100vh-4rem)]">
        {/* Desktop Sidebar */}
        <aside className="hidden md:flex w-64 border-r bg-muted/40 flex-col">
          <div className="p-6">
            <NavigationContent />
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 overflow-auto scroll-smooth-mobile safe-area-bottom">
          <div className="p-3 sm:p-4 md:p-6 lg:p-8 max-w-full container-responsive">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};